import { BsGrid3X2GapFill } from 'react-icons/bs';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import styled from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getEnvValueFor, getInitials } from '../../helpers/utils';
import {
  FaBoxes,
  FaTicketAlt,
  FaUtensils,
  FaShoppingCart,
  FaTruck,
  FaDesktop,
  FaMotorcycle,
  FaPhoneAlt
} from 'react-icons/fa';
import { rmsApi } from '../../state/axios';
import ThemeToggleSwitch from '../themeToggleSwitch';
import { IoIosColorPalette } from 'react-icons/io';
import { FiArrowLeft } from 'react-icons/fi';
import { authOperations } from '../../state/features/auth';
import { Spinner } from 'react-bootstrap';
import type { RootState } from '../../state/store';

interface DropdownDetails {
  apps: {
    id: string;
    name: string;
    url: string;
    icon: string;
  }[];
  user: {
    id: number;
    name: string;
    email: string;
    image_url: string;
  } | null;
}

const getAppIcon = (icon: string) => {
  switch (icon) {
    case 'fa-shopping-cart':
      return <FaShoppingCart size={18} />;
    case 'fa-cutlery':
      return <FaUtensils size={18} />;
    case 'fa-phone':
      return <FaPhoneAlt size={18} />;
    case 'fa-truck':
      return <FaTruck size={18} />;
    case 'fa-ticket':
      return <FaTicketAlt size={18} />;
    case 'fa-boxes':
      return <FaBoxes size={18} />;
    case 'fa-desktop':
      return <FaDesktop size={18} />;
    case 'fa-motorcycle':
      return <FaMotorcycle size={18} />;
    default:
      return null;
  }
};

const AppDrawer = () => {
  const sapaadCoreBaseUrl = getEnvValueFor('SAPAAD_CORE_BASE_URL') || '';
  const companyDetails = useSelector((state: RootState) => state.company?.company_details as any);
  const classicDashboardEnabled = Boolean(companyDetails?.classic_dashboard_enabled);
  const classicDashboardUrl = useMemo(() => {
    const baseUrl = sapaadCoreBaseUrl.replace(/\/$/, '');
    return baseUrl ? `${baseUrl}/dashboard` : '';
  }, [sapaadCoreBaseUrl]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [appDrawerDetails, setAppDrawerDetails] = useState<DropdownDetails | null>(null);
  const [imageLoadError, setImageLoadError] = useState<boolean>(false);

  useEffect(() => {
    fetchAppDrawerDetails();
  }, []);

  useEffect(() => {
    // Reset image error state when user image URL changes
    setImageLoadError(false);
  }, [appDrawerDetails?.user?.image_url]);

  const fetchAppDrawerDetails = async () => {
    setIsLoading(true);
    try {
      const response = await rmsApi.get('/api/v1/app-drawer-configuration');
      if (response.status === 200) {
        setAppDrawerDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching app drawer details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  const userDetails = useMemo(() => {
    return appDrawerDetails?.user;
  }, [appDrawerDetails]);

  const logoutCurrentUser = () => {
    const randomString = Math.random().toString(36).substring(2, 10);
    localStorage.setItem('jwt', randomString);
    localStorage.setItem('rt', randomString);
    authOperations.authorizeUser(false);
  };

  const handleLogout = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    logoutCurrentUser();
    // Redirect current tab to sign out URL
    setTimeout(() => {
      window.location.href = `${sapaadCoreBaseUrl}/users/sign_out`;
    }, 100);
  };

  const handleManageAccount = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openNewTab(`${sapaadCoreBaseUrl}/setup`);
  };

  const handleAppRedirection = (url: string) => {
    if (url) {
      //open in new tab
      openNewTab(url);
    }
  };

  const handleClassicDashboardSwitch = () => {
    if (classicDashboardUrl) {
      window.location.href = classicDashboardUrl;
    }
  };

  const showClassicDashboard = classicDashboardEnabled && Boolean(classicDashboardUrl);

  return (
    <Wrapper>
      <DropdownMenu.Root modal={false}>
        <Trigger aria-label="Open apps menu">
          <BsGrid3X2GapFill size={25} />
        </Trigger>
        <DropdownMenu.Portal>
          <Content
            side="bottom"
            align="end"
            sideOffset={8}
            $hasClassicFooter={showClassicDashboard}
          >
            {isLoading ? (
              <div className="loading-container">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <>
                <ScrollArea>
                  <MenuSection>
                    {appDrawerDetails?.apps?.map((app) => (
                      <MenuItem key={app.id} onClick={() => handleAppRedirection(app.url)}>
                        <div className="d-flex align-items-center gap-2">
                          <div className="app-icon">{getAppIcon(app.icon)}</div>
                          <div className="app-name">{app.name}</div>
                        </div>
                      </MenuItem>
                    ))}
                    <Separator />
                    <div className="theme-section d-flex align-items-center justify-content-between">
                      <span>
                        <IoIosColorPalette size={20} /> Theme
                      </span>
                      <ThemeToggleSwitch />
                    </div>
                  </MenuSection>
                  {userDetails && Object.keys(userDetails).length > 0 && (
                    <>
                      <Separator />
                      <div className="user-section">
                        <div className="user-info">
                          <div className="avatar">
                            {userDetails.image_url && !imageLoadError ? (
                              <img
                                src={userDetails.image_url}
                                alt={userDetails.name}
                                className="avatar-image"
                                onError={() => setImageLoadError(true)}
                              />
                            ) : (
                              <span className="avatar-initials">{getInitials(userDetails.name)}</span>
                            )}
                          </div>
                          <div className="user-info-text">
                            <div className="user-name">{userDetails.name}</div>
                            <div className="user-email">{userDetails.email}</div>
                          </div>
                        </div>
                        <div className="user-actions">
                          <a href="#" className="action-link" onClick={(e) => handleManageAccount(e)}>
                            Manage your account
                          </a>
                          <span className="separator-dot">•</span>
                          <a
                            href="#"
                            className="action-link logout"
                            onClick={(e) => {
                              handleLogout(e);
                            }}
                          >
                            Log-out
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                </ScrollArea>
                {showClassicDashboard && (
                  <ClassicDashboardFooter
                    onClick={handleClassicDashboardSwitch}
                    aria-label="Switch to Classic Dashboard"
                  >
                    <FiArrowLeft size={18} strokeWidth={1.75} aria-hidden />
                    Classic Dashboard
                  </ClassicDashboardFooter>
                )}
              </>
            )}
          </Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Trigger = styled(DropdownMenu.Trigger)`
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: #4e5969 !important;
  border: none !important;
  width: 40px !important;
  height: 40px !important;
  border-radius: 50% !important;
  cursor: pointer !important;
  color: #373a40 !important;
  transition: background-color 0.2s ease !important;
  padding: 0 !important;
  &:hover {
    background-color: #677387 !important;
  }
`;

const Content = styled(DropdownMenu.Content)<{ $hasClassicFooter?: boolean }>`
  background-color: ${({ theme }) => theme.dropdown.background_color};
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  padding: 0;
  z-index: 1000;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 16px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid ${({ theme }) => theme.dropdown.background_color};
    filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
  }
  .user-section {
    background-color: ${({ theme }) => theme.body};
    padding: 16px;
    border-bottom-left-radius: ${({ $hasClassicFooter }) => ($hasClassicFooter ? '0' : '16px')};
    border-bottom-right-radius: ${({ $hasClassicFooter }) => ($hasClassicFooter ? '0' : '16px')};
  }

  .user-info {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    gap: 12px;
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #7f71ce;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-initials {
    color: white;
    font-weight: 600;
    font-size: 14px;
  }

  .user-info-text {
    flex: 1;
    min-width: 0;
  }

  .user-name {
    font-weight: 700;
    font-size: 16px;
    color: ${({ theme }) => theme.text};
    margin-bottom: 4px;
  }

  .user-email {
    font-size: 14px;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }

  .user-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .action-link {
    font-size: 14px;
    text-decoration: underline;
    color: ${({ theme }) => theme.button.primary_color};
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.button.primary_color};
      opacity: 0.8;
    }

    &.logout {
      color: #dc2626;

      &:hover {
        color: #b91c1c;
      }
    }
  }

  .separator-dot {
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    font-size: 14px;
  }

  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
  }
`;

const MenuSection = styled.div`
  padding: 8px 0;
  .theme-section {
    padding: 12px 16px;
    font-size: 16px;
    background-color: ${({ theme }) => theme.theme_toggle_option};
    color: ${({ theme }) => theme.dropdown.color};
    cursor: pointer;
    transition: background-color 0.2s ease;
    outline: none;
    display: flex;
  }
`;

const MenuItem = styled(DropdownMenu.Item)<{ $isLast?: boolean }>`
  padding: 12px 16px;
  font-size: 16px;
  color: ${({ theme }) => theme.dropdown.color};
  cursor: pointer;
  transition: background-color 0.2s ease;
  outline: none;
  border-bottom: 1px solid ${({ theme }) => theme.border_color};

  &:hover,
  &[data-highlighted] {
    background-color: ${({ theme }) => theme.modal.hover};
    color: ${({ theme }) => theme.dropdown.color};
  }
`;

const Separator = styled(DropdownMenu.Separator)`
  height: 1px;
  background-color: ${({ theme }) => theme.border_color};
  margin: 0;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
`;

const ClassicDashboardFooter = styled(DropdownMenu.Item)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  padding: 16px 20px;
  margin: 0;
  border: none;
  border-radius: 0;
  background-color: #5c594f;
  color: #ffffff;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  outline: none;

  &:hover,
  &[data-highlighted] {
    background-color: #4d4a41;
    color: #ffffff;
    border-radius: 0;
  }

  svg {
    flex-shrink: 0;
    display: block;
  }
`;

export default AppDrawer;
